import React, { useState, useRef, useEffect } from "react";
import io from "socket.io-client";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

const ChatDetails = () => {
  // Chat messages - newest messages at the bottom
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const messagesContainerRef = useRef(null);
  const { id } = useParams();
  const { admin } = useSelector((state) => state.userSlice);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const socketRef = useRef();

  // connect with sockets
  useEffect(() => {
    if (Object.keys(admin).length === 0) return;
    
    console.log("Initializing Socket.IO connection...");
    
    // Initialize Socket.IO connection
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      extraHeaders: {
        token: localStorage.getItem("sgtoken"),
      },
    });

    // Add event listeners
    const handleConnect = () => {
      console.log("Connected Successfully");
      socket.emit("adduser", { id: admin._id });
      socket.emit("joinchat", { id: id });
      loadMessages();
    };

    const handleNewMessage = (response) => {
      console.log("Received new message:", response);
      if (isScrolledUp) {
        setShowNewMessageAlert(true);
      }
      loadMessages();
    };

    socket.on("connect", handleConnect);
    socket.on("receivemessage", handleNewMessage);

    socketRef.current = socket;

    // Cleanup function
    return () => {
      console.log("Disconnecting now");
      socket.off("connect", handleConnect);
      socket.off("receivemessage", handleNewMessage);
      socket.disconnect();
    };
  }, [id, admin]);

  const loadMessages = (loadMore = false) => {
    if (loadMore) {
      if (!hasMore || isLoadingMore) return;
      setIsLoadingMore(true);
    } else if (!loadMore && messages.length === 0) {
      // Only set loading to true if it's initial load and no messages yet
      setIsLoading(true);
    }

    const currentPage = loadMore ? page + 1 : 1;
    const limit = 15;
    const skip = (currentPage - 1) * limit;

    socketRef.current.emit("getmessagelistadmin", { id, skip, limit }, (response) => {
      if (response && response.data) {
        const sortedMessages = response.data.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

        setMessages(prevMessages => {
          // For initial load or new messages, replace the messages
          if (!loadMore) return sortedMessages;
          
          // For loading more, prepend older messages
          return [...sortedMessages, ...prevMessages];
        });

        // Set chat user from the first message if not set
        if (sortedMessages.length > 0 && !chatUser) {
          const userMessage = sortedMessages.find(
            msg => (msg.sender && msg.sender._id !== admin._id) ||
                   (msg.chatModal && msg.chatModal.senderId !== admin._id)
          );

          if (userMessage) {
            if (userMessage.sender) {
              setChatUser(userMessage.sender);
            } else if (userMessage.chatModal?.sender?.[0]) {
              setChatUser(userMessage.chatModal.sender[0]);
            }
          }
        }

        // Check if there are more messages to load
        setHasMore(response.data.length === limit);
        if (loadMore) {
          setPage(currentPage);
        }
      } else {
        setMessages([]);
      }
      
      setIsLoading(false);
      setIsLoadingMore(false);
    });
  };

  // Handle scroll events for infinite loading
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop } = container;
      // If scrolled to top and not currently loading more
      if (scrollTop < 100 && !isLoadingMore && hasMore) {
        loadMessages(true);
      }
      
      // Check if user has scrolled up
      const { scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 100;
      setIsScrolledUp(!isAtBottom);
      
      if (isAtBottom) {
        setShowNewMessageAlert(false);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, hasMore]);

  // Auto-scroll to bottom on initial load or new message
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || isLoading) return;
    
    // Only auto-scroll if not scrolled up or if it's a new message
    if (!isScrolledUp || messages.some((msg) => msg.isTemp)) {
      scrollToBottom();
      setShowNewMessageAlert(false);
    }
  }, [messages, isScrolledUp]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";

      const defaultHeight = 40;
      const scrollHeight = textareaRef.current.scrollHeight;

      if (scrollHeight > defaultHeight) {
        textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
      } else {
        textareaRef.current.style.height = `${defaultHeight}px`;
      }
    }
  }, [newMessage]);

  const scrollToBottom = () => {
    // Ensure scroll happens after DOM update
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim() === "") return;

    const messageData = {
      message: newMessage,
      type: "text",
      createdAt: new Date().toISOString(),
      _id: `temp-${Date.now()}`,
      isTemp: true,
    };

    setMessages([...messages, messageData]);

    const sendMeaageData = {
      chatId: id,
      message: newMessage,
      type: "text",
      sendBy: admin._id,
      receiverId: chatUser._id,
    };
    socketRef.current.emit("sendmessage", sendMeaageData, (response) => {
      if (response) {
        socketRef.current.emit("newmessage", sendMeaageData);
      } else {
        console.warn(
          "No response from sendmessage, callback might not be working"
        );
      }
    });

    // Add a fallback in case the callback doesn't work
    setTimeout(() => {
      console.log("Checking if message was sent and callback executed");
      loadMessages();
    }, 1000);
    setNewMessage("");
  };

  return (
    <div className="chat-details-container" style={styles.container}>
      <div className="chat-header" style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.avatarContainer}>
            {chatUser && chatUser.userImage ? (
              <img
                src={chatUser.userImage}
                alt={chatUser.firstName}
                style={styles.avatarImage}
              />
            ) : (
              <div style={styles.avatar}>
                {chatUser ? chatUser.firstName.charAt(0) : "U"}
              </div>
            )}
          </div>
          <div style={styles.headerInfo}>
            <h4 style={styles.headerName}>
              {chatUser ? `${chatUser.firstName} ${chatUser.lastName}` : "User"}
            </h4>
          </div>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className="messages-container"
        style={styles.messagesContainer}
      >
        {showNewMessageAlert && (
          <div style={styles.newMessageAlert} onClick={scrollToBottom}>
            <span>New messages</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5L12 19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19 12L12 19L5 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        <div style={styles.messagesWrapper}>
          {isLoadingMore && (
            <div style={styles.loadingMoreContainer}>
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span>Loading older messages...</span>
            </div>
          )}
          {isLoading ? (
            <div style={styles.loadingContainer}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : messages?.length === 0 ? (
            <div style={styles.emptyContainer}>No messages yet</div>
          ) : (
            messages.map((message) => {
              // Determine if message is from admin (self) or user
              // Check if the message is from the admin based on sender ID
              const isSelf =
                message.isTemp ||
                (message.sender && message.sender._id === admin._id) ||
                message.sendBy === admin._id ||
                (message.chatModal && message.chatModal.senderId === admin._id);

              // Get sender name
              let senderName = "User";
              if (message.sender && message.sender.firstName) {
                // Use sender object directly if available with name
                senderName = `${message.sender.firstName} ${
                  message.sender.lastName || ""
                }`;
              } else if (
                message.chatModal &&
                message.chatModal.sender &&
                message.chatModal.sender.length > 0
              ) {
                // Fallback to chatModal.sender
                const sender = message.chatModal.sender[0];
                senderName = `${sender.firstName} ${sender.lastName || ""}`;
              }

              // Format timestamp WhatsApp style
              const messageDate = new Date(message.createdAt);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              
              let timestamp;
              if (messageDate.toDateString() === today.toDateString()) {
                // Today: show only time
                timestamp = messageDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });
              } else if (messageDate.toDateString() === yesterday.toDateString()) {
                // Yesterday: show "Yesterday" and time
                timestamp = `Yesterday, ${messageDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;
              } else if (today.getFullYear() === messageDate.getFullYear()) {
                // Same year: show day/month and time
                timestamp = `${messageDate.toLocaleDateString([], {
                  month: "short",
                  day: "numeric",
                })}, ${messageDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;
              } else {
                // Different year: show day/month/year and time
                timestamp = `${messageDate.toLocaleDateString([], {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}, ${messageDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;
              }

              return (
                <div
                  key={message._id}
                  className={`message ${isSelf ? "self" : "other"}`}
                  style={{
                    ...styles.message,
                    ...(isSelf ? styles.selfMessage : styles.otherMessage),
                  }}
                >
                  <div
                    className="message-content"
                    style={{
                      ...styles.messageContent,
                      ...(isSelf
                        ? styles.selfMessageContent
                        : styles.otherMessageContent),
                    }}
                  >
                    <div className="text" style={styles.text}>
                      {message.message}
                    </div>
                    <div className="timestamp" style={styles.timestamp}>
                      {timestamp}
                      {isSelf && (
                        <span style={{
                          ...styles.checkmark,
                          color: message.isTemp ? '#A8ABAF' : 
                                 message.readBy && message.readBy.some(id => id !== admin._id) ? '#34B7F1' : '#A8ABAF'
                        }}>
                          {message.isTemp ? (
                            // Clock icon for pending messages
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          ) : message.readBy && message.readBy.some(id => id !== admin._id) ? (
                            // Double blue checkmark for read messages
                            <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.071 0.653L4.29397 7.42999L1.99997 5.13599" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14.001 0.653L7.22399 7.42999L5.37299 5.57899" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            // Double gray checkmark for delivered but unread messages
                            <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.071 0.653L4.29397 7.42999L1.99997 5.13599" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14.001 0.653L7.22399 7.42999L5.37299 5.57899" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <form
        className="message-input"
        style={styles.inputContainer}
        onSubmit={handleSendMessage}
      >
        <textarea
          ref={textareaRef}
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          style={styles.input}
          rows="1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
        />
        <button type="submit" style={styles.sendButton}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 2L11 13"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 2L15 22L11 13L2 9L22 2Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
};

// Inline styles
const styles = {
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    padding: "20px",
  },
  loadingMoreContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px",
    gap: "10px",
    color: "#667781",
    fontSize: "14px",
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    color: "#667781",
    fontSize: "14px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 120px)",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "#f0f2f5", // WhatsApp background color
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  header: {
    padding: "10px 16px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#f0f0f0",
    display: "flex",
    alignItems: "center",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  avatarContainer: {
    marginRight: "15px",
  },
  avatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#25D366", // WhatsApp green
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: "18px",
  },
  avatarImage: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  headerInfo: {
    display: "flex",
    flexDirection: "column",
  },
  headerName: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "bold",
  },
  headerStatus: {
    fontSize: "12px",
    color: "#667781",
  },
  messagesContainer: {
    flex: 1,
    padding: "16px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column-reverse", // This makes the messages start from bottom
    backgroundImage:
      "url('https://web.whatsapp.com/img/bg-chat-tile-light_a4be512e7195b6b733d9110b408f075d.png')",
    backgroundRepeat: "repeat",
    backgroundSize: "contain",
  },
  messagesWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minHeight: "100%",
    justifyContent: "flex-end",
  },
  newMessageAlert: {
    position: "absolute",
    bottom: "80px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#128c7e",
    color: "white",
    padding: "8px 16px",
    borderRadius: "20px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    zIndex: 10,
    fontSize: "14px",
  },
  message: {
    maxWidth: "65%",
    marginBottom: "4px",
    display: "flex",
    position: "relative",
  },
  selfMessage: {
    alignSelf: "flex-end",
  },
  otherMessage: {
    alignSelf: "flex-start",
  },
  messageContent: {
    padding: "8px 12px",
    borderRadius: "8px",
    position: "relative",
    boxShadow: "0 1px 0.5px rgba(0, 0, 0, 0.13)",
  },
  selfMessageContent: {
    backgroundColor: "#dcf8c6", // WhatsApp green bubble
    borderTopRightRadius: "0",
  },
  otherMessageContent: {
    backgroundColor: "#ffffff",
    borderTopLeftRadius: "0",
  },
  sender: {
    fontWeight: "bold",
    marginBottom: "4px",
    fontSize: "13px",
    color: "#128c7e",
  },
  text: {
    wordBreak: "break-word",
    fontSize: "14px",
    lineHeight: "19px",
  },
  timestamp: {
    fontSize: "11px",
    opacity: 0.7,
    marginTop: "2px",
    marginLeft: "8px",
    float: "right",
    color: "#667781",
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  checkmark: {
    display: "inline-block",
    marginLeft: "4px",
    verticalAlign: "middle",
    float: "right",
    lineHeight: 0,
  },
  inputContainer: {
    display: "flex",
    padding: "10px 16px",
    backgroundColor: "#f0f0f0",
    alignItems: "flex-end",
  },
  input: {
    flex: 1,
    padding: "9px 12px",
    border: "1px solid #ddd",
    borderRadius: "20px",
    outline: "none",
    fontSize: "15px",
    resize: "none",
    maxHeight: "120px",
    minHeight: "20px",
    lineHeight: "20px",
    fontFamily: "inherit",
    backgroundColor: "white",
  },
  sendButton: {
    marginLeft: "8px",
    width: "40px",
    height: "40px",
    backgroundColor: "#00a884",
    color: "white",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
};

export default ChatDetails;
